import { create } from "zustand"
import type {
  AgentRuntimeSnapshot,
  AgentRuntimeInteractionRequest,
  AgentRuntimeStatus,
  AgentRuntimeCapabilities,
  AgentRuntimeActionAvailability,
  AgentType,
} from "@cord/shared"
import type { CursorRawProjection } from "@/lib/cursor-raw-runtime"
import {
  applyClaudeBaselineSyncEventsBatch,
  closeClaudeStreamingTimelineItems,
  type ClaudeRawProjection,
} from "@/lib/claude-raw-runtime"
import {
  buildOpenCodeSnapshot,
  type OpenCodeRawProjection,
} from "@/lib/opencode-raw-runtime"
import { shouldTreatOpenStreamingTurnAsIncomplete } from "@/lib/opencode-derived-state"
import { messageHasOpenRenderableAssistantPart } from "@/lib/opencode-runtime-shared"
import { reconcileRuntimeSnapshot } from "@/lib/runtime-snapshot-stability"
import {
  computeRawSyncEventsBatch,
  type AgentRuntimeComputationState,
  type ComputedRawSyncEventsBatch,
} from "@/lib/agent-runtime-sync-batch-computation"

export interface AgentRuntimeStoreSnapshot {
  agentType: AgentType | null
  snapshot: AgentRuntimeSnapshot | null
  lastAppliedTimestamp: number | null
  lastEventId: number | null
}

interface AgentRuntimeStoreState extends AgentRuntimeStoreSnapshot {
  stateVersion: number
  rawOpenCodeProjection: OpenCodeRawProjection | null
  rawClaudeProjection: ClaudeRawProjection | null
  rawCursorProjection: CursorRawProjection | null
  applyRawSyncEvent: (input: {
    eventType: string
    payload: unknown
    eventId: number
    childSessionId?: string | null
    timestamp?: number
  }) => boolean
  applyRawSyncEventsBatch: (events: Array<{
    eventType: string
    payload: unknown
    eventId: number
    childSessionId?: string | null
    timestamp?: number
  }>) => {
    appliedCount: number
    maxEventId: number | null
    agentType: AgentType | null
  }
  applyBaselineEvents: (input: {
    agentType: AgentType
    events: Array<{
      baselineSeq: number
      eventType: string
      payload: unknown
      timestamp?: number
      childSessionId?: string | null
    }>
  }) => {
    status: "applied" | "noop" | "error"
    appliedCount: number
  }
  applyControlPlaneSnapshot: (input: {
    agentType: AgentType
    pendingInteractions: AgentRuntimeInteractionRequest[]
    status: AgentRuntimeStatus
    conversationEpoch?: number | null
  }) => void
  commitComputedRawSyncEventsBatch: (input: {
    computed: ComputedRawSyncEventsBatch
    expectedStateVersion: number
  }) => boolean
  commitRuntimeState: (input: {
    nextState: AgentRuntimeComputationState
    expectedStateVersion: number
  }) => boolean
  reset: () => void
}

function normalizeProviderStatusTypeForDerivedStateInvalidation(
  providerStatusType: unknown,
): string | null {
  if (typeof providerStatusType !== "string") return null

  const normalizedProviderStatusType = providerStatusType.toLowerCase()
  if (
    normalizedProviderStatusType === "busy"
    || normalizedProviderStatusType === "running"
    || normalizedProviderStatusType === "retry"
    || normalizedProviderStatusType === "working"
  ) {
    return "running"
  }

  return normalizedProviderStatusType
}

function rawOpenCodeProjectionHasOpenRenderableAssistantPart(
  projection: OpenCodeRawProjection | null | undefined,
): boolean {
  if (!projection) return false

  return Object.values(projection.messagesById).some((message) => messageHasOpenRenderableAssistantPart({
    partsById: message.partsById,
  }))
}

function createAvailability(enabled: boolean, disabledReason: string | null): AgentRuntimeActionAvailability {
  return {
    available: true,
    enabled,
    disabledReason: enabled ? null : disabledReason,
  }
}

function buildFallbackCapabilities(agentType: AgentType): AgentRuntimeCapabilities {
  if (agentType === "cursor") {
    return {
      agentType: "cursor",
      supportsPromptParts: true,
      supportsImageAttachments: true,
      supportsSlashCommands: false,
      supportsSessionActions: false,
      supportsQuestions: true,
      supportsPermissions: true,
      supportsUsage: false,
      supportsForkFromMessage: false,
      supportsEditFromMessage: false,
      supportsSubagents: true,
    }
  }

  if (agentType === "claude_code") {
    return {
      agentType: "claude_code",
      supportsPromptParts: true,
      supportsImageAttachments: true,
      supportsSlashCommands: true,
      supportsSessionActions: true,
      supportsQuestions: false,
      supportsPermissions: true,
      supportsUsage: true,
      supportsForkFromMessage: true,
      supportsEditFromMessage: true,
      supportsSubagents: true,
    }
  }

  return {
    agentType,
    supportsPromptParts: true,
    supportsImageAttachments: true,
    supportsSlashCommands: true,
    supportsSessionActions: true,
    supportsQuestions: true,
    supportsPermissions: true,
    supportsUsage: true,
    supportsForkFromMessage: true,
    supportsEditFromMessage: true,
    supportsSubagents: true,
  }
}

function resolvePersistedOpenCodeSessionError(args: {
  controlPlaneStatus: AgentRuntimeStatus
  rawProjection: OpenCodeRawProjection | null
}): { type: string; message: string } | null {
  if (args.controlPlaneStatus.error?.message) {
    return {
      type: args.controlPlaneStatus.error.type ?? "runtime_error",
      message: args.controlPlaneStatus.error.message,
    }
  }

  if (args.controlPlaneStatus.phase !== "error") {
    return null
  }

  const existingError = args.rawProjection?.lastSessionError
  if (!existingError?.message) {
    return null
  }

  return {
    type: existingError.type ?? "runtime_error",
    message: existingError.message,
  }
}

function emptyStoreSnapshot(): AgentRuntimeStoreSnapshot {
  return {
    agentType: null,
    snapshot: null,
    lastAppliedTimestamp: null,
    lastEventId: null,
  }
}

function buildComputationStateFromStore(
  state: Pick<
    AgentRuntimeStoreState,
    | "agentType"
    | "snapshot"
    | "lastAppliedTimestamp"
    | "lastEventId"
    | "rawOpenCodeProjection"
    | "rawClaudeProjection"
    | "rawCursorProjection"
  >,
): AgentRuntimeComputationState {
  return {
    agentType: state.agentType,
    snapshot: state.snapshot,
    lastAppliedTimestamp: state.lastAppliedTimestamp,
    lastEventId: state.lastEventId,
    rawOpenCodeProjection: state.rawOpenCodeProjection,
    rawClaudeProjection: state.rawClaudeProjection,
    rawCursorProjection: state.rawCursorProjection,
  }
}

export const useAgentRuntime = create<AgentRuntimeStoreState>((set, get) => ({
  ...emptyStoreSnapshot(),
  stateVersion: 0,
  rawOpenCodeProjection: null,
  rawClaudeProjection: null,
  rawCursorProjection: null,

  applyRawSyncEventsBatch: (events) => {
    const state = get()
    const computed = computeRawSyncEventsBatch({
      currentState: buildComputationStateFromStore(state),
      events,
    })

    if (computed.didChange) {
      set((currentState) => ({
        ...computed.nextState,
        stateVersion: currentState.stateVersion + 1,
      }))
    }

    return {
      appliedCount: computed.appliedCount,
      maxEventId: computed.maxEventId,
      agentType: computed.agentType,
    }
  },

  applyRawSyncEvent: (input) => {
    const result = get().applyRawSyncEventsBatch([input])
    return result.appliedCount > 0
  },

  applyBaselineEvents: (input) => {
    if (input.agentType !== "claude_code") {
      return { status: "noop", appliedCount: 0 }
    }

    const state = get()
    const previousSnapshot = state.agentType === input.agentType ? state.snapshot : null
    const existingBaselineIds = new Set<number>()
    for (const entry of state.rawClaudeProjection?.events ?? []) {
      if (typeof entry.id === "number" && Number.isFinite(entry.id) && entry.id < 0) {
        existingBaselineIds.add(entry.id)
      }
    }

    const baselineEvents = input.events
      .map((event) => {
        const baselineSeq =
          typeof event.baselineSeq === "number" && Number.isFinite(event.baselineSeq) && event.baselineSeq > 0
            ? Math.floor(event.baselineSeq)
            : 0
        if (baselineSeq <= 0) return null
        if (existingBaselineIds.has(-baselineSeq)) return null
        return {
          baselineSeq,
          eventType: event.eventType,
          payload: event.payload,
          timestamp:
            typeof event.timestamp === "number" && Number.isFinite(event.timestamp)
              ? Math.floor(event.timestamp)
              : Date.now(),
        }
      })
      .filter((event): event is NonNullable<typeof event> => event !== null)

    if (baselineEvents.length === 0) {
      return { status: "noop", appliedCount: 0 }
    }

    const applied = applyClaudeBaselineSyncEventsBatch({
      projection: state.rawClaudeProjection,
      events: baselineEvents,
    })
    if (!applied) {
      return { status: "error", appliedCount: 0 }
    }

    const stableProviderEventId =
      typeof previousSnapshot?.cursorState.providerEventId === "number" &&
      Number.isFinite(previousSnapshot.cursorState.providerEventId) &&
      previousSnapshot.cursorState.providerEventId > 0
        ? Math.floor(previousSnapshot.cursorState.providerEventId)
        : state.lastEventId
    const baselineSnapshot: AgentRuntimeSnapshot = {
      ...applied.snapshot,
      cursorState: {
        ...applied.snapshot.cursorState,
        providerEventId: stableProviderEventId ?? null,
      },
    }

    set((currentState) => ({
      agentType: input.agentType,
      snapshot: reconcileRuntimeSnapshot(previousSnapshot, baselineSnapshot),
      rawOpenCodeProjection: null,
      rawClaudeProjection: applied.projection as ClaudeRawProjection,
      rawCursorProjection: null,
      lastAppliedTimestamp:
        typeof state.lastAppliedTimestamp === "number" && state.lastAppliedTimestamp > 0
          ? Math.max(state.lastAppliedTimestamp, applied.lastAppliedTimestamp)
          : applied.lastAppliedTimestamp,
      lastEventId: state.lastEventId,
      stateVersion: currentState.stateVersion + 1,
    }))

    return { status: "applied", appliedCount: applied.appliedCount }
  },

  applyControlPlaneSnapshot: (input) => {
    set((state) => {
      const existingSnapshot = state.agentType === input.agentType ? state.snapshot : null
      const capabilities = existingSnapshot?.capabilities ?? buildFallbackCapabilities(input.agentType)
      const resolvedOpenCodeSessionError = input.agentType === "opencode"
        ? resolvePersistedOpenCodeSessionError({
            controlPlaneStatus: input.status,
            rawProjection: state.rawOpenCodeProjection,
          })
        : null
      const statusError = input.agentType === "opencode"
        ? resolvedOpenCodeSessionError
        : input.status.error
      const providerStatusRecord: Record<string, unknown> = {
        type:
          input.status.phase === "running"
            ? "busy"
            : input.status.phase === "waiting_input"
              ? "waiting_input"
              : input.status.phase,
      }
      if (typeof input.status.startedAt === "number") {
        providerStatusRecord.startedAt = input.status.startedAt
      }
      if (typeof input.status.finishedAt === "number") {
        providerStatusRecord.finishedAt = input.status.finishedAt
      }
      if (typeof input.status.finishReason === "string") {
        providerStatusRecord.finishReason = input.status.finishReason
      }
      if (statusError?.message) {
        providerStatusRecord.message = statusError.message
      }
      if (statusError?.type) {
        providerStatusRecord.errorType = statusError.type
      }
      if (input.status.replicationIntegrity) {
        providerStatusRecord.replicationIntegrity = {
          state: input.status.replicationIntegrity.state,
          reason: input.status.replicationIntegrity.reason ?? null,
          detectedAt: input.status.replicationIntegrity.detectedAt ?? null,
        }
      }
      if (input.status.runtimeFinality) {
        providerStatusRecord.runtimeFinality = {
          state: input.status.runtimeFinality.state,
          reason: input.status.runtimeFinality.reason ?? null,
        }
      }

      let preservedClaudeStartedAt: number | null = null

      if (input.agentType === "claude_code") {
        const existingProjectionStartedAt =
          typeof state.rawClaudeProjection?.providerStatus?.startedAt === "number"
          && Number.isFinite(state.rawClaudeProjection.providerStatus.startedAt)
            ? state.rawClaudeProjection.providerStatus.startedAt
            : null
        const existingSnapshotStartedAt =
          typeof existingSnapshot?.status.startedAt === "number"
          && Number.isFinite(existingSnapshot.status.startedAt)
            ? existingSnapshot.status.startedAt
            : null
        const incomingStartedAt =
          typeof providerStatusRecord.startedAt === "number"
          && Number.isFinite(providerStatusRecord.startedAt)
            ? providerStatusRecord.startedAt
            : null

        if (
          existingProjectionStartedAt !== null && (
            incomingStartedAt === null ||
            ((input.status.phase === "running" || input.status.phase === "waiting_input")
              && incomingStartedAt > existingProjectionStartedAt)
          )
        ) {
          // Claude waiting_input control-plane snapshots can derive startedAt
          // from the activity transition (permission boundary), which is later
          // than the actual run start recorded by the raw runtime projection.
          // Preserve the earlier anchor so queued/transcript handoff stays tied
          // to the original prompt even after Ask User pauses the run.
          providerStatusRecord.startedAt = existingProjectionStartedAt
          preservedClaudeStartedAt = existingSnapshotStartedAt
        }
      }

      const existingReplicationIntegrity = input.agentType === "opencode"
        ? state.rawOpenCodeProjection?.providerStatus?.replicationIntegrity as Record<string, unknown> | undefined
        : undefined
      const existingRuntimeFinality = input.agentType === "opencode"
        ? state.rawOpenCodeProjection?.providerStatus?.runtimeFinality as Record<string, unknown> | undefined
        : undefined
      const existingProviderStatusType = input.agentType === "opencode"
        ? normalizeProviderStatusTypeForDerivedStateInvalidation(
            state.rawOpenCodeProjection?.providerStatus?.type,
          )
        : null
      const nextShouldForceCloseOpenStreamingParts = input.agentType === "opencode"
        ? shouldTreatOpenStreamingTurnAsIncomplete({
            providerStatus: providerStatusRecord,
            pendingInteractionCount: input.pendingInteractions.length,
            hasOpenRenderableAssistantPart: rawOpenCodeProjectionHasOpenRenderableAssistantPart(
              state.rawOpenCodeProjection,
            ),
          })
        : false
      const existingShouldForceCloseOpenStreamingParts = input.agentType === "opencode"
        ? shouldTreatOpenStreamingTurnAsIncomplete({
            providerStatus: state.rawOpenCodeProjection?.providerStatus as Record<string, unknown> | null,
            pendingInteractionCount:
              Object.keys(state.rawOpenCodeProjection?.pendingPermissionsById ?? {}).length
              + Object.keys(state.rawOpenCodeProjection?.pendingQuestionsById ?? {}).length,
            hasOpenRenderableAssistantPart: rawOpenCodeProjectionHasOpenRenderableAssistantPart(
              state.rawOpenCodeProjection,
            ),
          })
        : false
      const shouldInvalidateOpenCodeDerivedState = input.agentType === "opencode" && (
        existingProviderStatusType
          !== normalizeProviderStatusTypeForDerivedStateInvalidation(providerStatusRecord.type) ||
        existingReplicationIntegrity?.state !== (providerStatusRecord.replicationIntegrity as Record<string, unknown> | undefined)?.state ||
        existingReplicationIntegrity?.reason !== (providerStatusRecord.replicationIntegrity as Record<string, unknown> | undefined)?.reason ||
        existingReplicationIntegrity?.detectedAt !== (providerStatusRecord.replicationIntegrity as Record<string, unknown> | undefined)?.detectedAt ||
        existingRuntimeFinality?.state !== (providerStatusRecord.runtimeFinality as Record<string, unknown> | undefined)?.state ||
        existingRuntimeFinality?.reason !== (providerStatusRecord.runtimeFinality as Record<string, unknown> | undefined)?.reason ||
        existingShouldForceCloseOpenStreamingParts !== nextShouldForceCloseOpenStreamingParts
      )

      let nextRawOpenCodeProjection = input.agentType === "opencode" ? state.rawOpenCodeProjection : null
      let nextRawClaudeProjection = input.agentType === "claude_code" ? state.rawClaudeProjection : null
      let nextRawCursorProjection = input.agentType === "cursor" ? state.rawCursorProjection : null

      if (input.agentType === "opencode" && state.rawOpenCodeProjection) {
        const pendingPermissionsById: Record<string, Record<string, unknown>> = {}
        const pendingQuestionsById: Record<string, Record<string, unknown>> = {}
        for (const interaction of input.pendingInteractions) {
          if ((interaction.kind === "permission" || interaction.kind === "ask_user") && interaction.raw) {
            pendingPermissionsById[interaction.requestId] = interaction.raw
          }
          if (interaction.kind === "question" && interaction.raw) {
            pendingQuestionsById[interaction.requestId] = interaction.raw
          }
        }
        nextRawOpenCodeProjection = {
          ...state.rawOpenCodeProjection,
          providerStatus: providerStatusRecord,
          lastSessionError: resolvedOpenCodeSessionError,
          pendingPermissionsById,
          pendingQuestionsById,
        }
        if (shouldInvalidateOpenCodeDerivedState) {
          nextRawOpenCodeProjection.derivedStateVersion = 0
        }
      }

      if (input.agentType === "claude_code" && state.rawClaudeProjection) {
        const pendingPermissionsById: Record<string, Record<string, unknown>> = {}
        for (const interaction of input.pendingInteractions) {
          if ((interaction.kind === "permission" || interaction.kind === "ask_user") && interaction.raw) {
            pendingPermissionsById[interaction.requestId] = interaction.raw
          }
        }
        nextRawClaudeProjection = {
          ...state.rawClaudeProjection,
          providerStatus: providerStatusRecord,
          pendingPermissionsById,
        }
      }

      if (input.agentType === "cursor" && state.rawCursorProjection) {
        const pendingPermissionsById: Record<string, Record<string, unknown>> = {}
        const pendingQuestionsById: Record<string, { kind: "question" | "plan"; sessionId: string | null; raw: Record<string, unknown> }> = {}
        for (const interaction of input.pendingInteractions) {
          if ((interaction.kind === "permission" || interaction.kind === "ask_user") && interaction.raw) {
            pendingPermissionsById[interaction.requestId] = interaction.raw
          }
          if (interaction.kind === "question" && interaction.raw) {
            pendingQuestionsById[interaction.requestId] = {
              kind: interaction.raw.kind === "plan" ? "plan" : "question",
              sessionId: interaction.providerRef?.sessionId ?? null,
              raw: interaction.raw,
            }
          }
        }
        nextRawCursorProjection = {
          ...state.rawCursorProjection,
          providerStatus: providerStatusRecord,
          pendingPermissionsById,
          pendingQuestionsById,
        }
      }

      const effectiveStatus = input.agentType === "opencode" && resolvedOpenCodeSessionError
        ? {
            ...input.status,
            error: resolvedOpenCodeSessionError,
          }
        : input.status
      const effectiveRuntimeStatus = input.agentType === "claude_code"
        ? {
            ...effectiveStatus,
            startedAt: preservedClaudeStartedAt ?? effectiveStatus.startedAt,
          }
        : effectiveStatus

      const busy = effectiveRuntimeStatus.phase === "running"
      const hasPendingPermission = input.pendingInteractions.some(
        (interaction) => interaction.kind === "permission" || interaction.kind === "ask_user",
      )
      const hasPendingQuestion = input.pendingInteractions.some(
        (interaction) => interaction.kind === "question",
      )

      const nextConversationEpoch =
        typeof input.conversationEpoch === "number" && Number.isFinite(input.conversationEpoch)
          ? Math.max(0, Math.floor(input.conversationEpoch))
          : existingSnapshot?.conversationEpoch ?? 0
      const nextActions = {
        availability: {
          ...(existingSnapshot?.actions.availability ?? {}),
          abort: createAvailability(busy, "Nothing to abort right now"),
          reply_permission: createAvailability(
            capabilities.supportsPermissions && hasPendingPermission,
            "No pending permission request",
          ),
          reply_question: createAvailability(
            capabilities.supportsQuestions && hasPendingQuestion,
            "No pending question",
          ),
          question_reject: createAvailability(
            capabilities.supportsQuestions && hasPendingQuestion,
            "No pending question",
          ),
        },
        revertMessageId: existingSnapshot?.actions.revertMessageId ?? null,
      }

      const nextSnapshot: AgentRuntimeSnapshot = input.agentType === "opencode" && nextRawOpenCodeProjection
        ? (() => {
            const rebuiltSnapshot = buildOpenCodeSnapshot(
              nextRawOpenCodeProjection,
              existingSnapshot?.cursorState.providerEventId ?? state.lastEventId ?? 0,
            )
            const shouldPreserveRebuiltOpenCodeStatus =
              rebuiltSnapshot.status.phase === "running"
              // A stale control-plane idle can land before the raw transcript has
              // actually closed its streaming turn, but waiting_input should win
              // immediately so we do not keep stale stop/thinking UI visible once
              // the runtime has transitioned into an interaction pause.
              && effectiveStatus.phase === "idle"
            const nextOpenCodeStatus = shouldPreserveRebuiltOpenCodeStatus
              ? rebuiltSnapshot.status
              : effectiveStatus
            const openCodeBusy = nextOpenCodeStatus.phase === "running"
            const nextOpenCodeActions = {
              availability: {
                ...rebuiltSnapshot.actions.availability,
                abort: createAvailability(openCodeBusy, "Nothing to abort right now"),
                reply_permission: createAvailability(
                  capabilities.supportsPermissions && hasPendingPermission,
                  "No pending permission request",
                ),
                reply_question: createAvailability(
                  capabilities.supportsQuestions && hasPendingQuestion,
                  "No pending question",
                ),
                question_reject: createAvailability(
                  capabilities.supportsQuestions && hasPendingQuestion,
                  "No pending question",
                ),
              },
              revertMessageId: rebuiltSnapshot.actions.revertMessageId,
            }
            return {
              ...rebuiltSnapshot,
              pendingMessages: existingSnapshot?.pendingMessages ?? [],
              pendingInteractions: input.pendingInteractions,
              status: nextOpenCodeStatus,
              actions: nextOpenCodeActions,
              activeEditOperation: existingSnapshot?.activeEditOperation ?? null,
              conversationEpoch: nextConversationEpoch,
              sessionActionOutcome: existingSnapshot?.sessionActionOutcome ?? null,
              capabilities,
            }
          })()
        : {
            timeline:
              input.agentType === "claude_code"
                ? closeClaudeStreamingTimelineItems({
                    timeline: existingSnapshot?.timeline ?? [],
                    statusPhase: effectiveRuntimeStatus.phase,
                  })
                : existingSnapshot?.timeline ?? [],
            pendingMessages: existingSnapshot?.pendingMessages ?? [],
            pendingInteractions: input.pendingInteractions,
            status: effectiveRuntimeStatus,
            usage: existingSnapshot?.usage ?? {},
            actions: nextActions,
            activeEditOperation: existingSnapshot?.activeEditOperation ?? null,
            conversationEpoch: nextConversationEpoch,
            sessionActionOutcome: existingSnapshot?.sessionActionOutcome ?? null,
            capabilities,
            cursorState: existingSnapshot?.cursorState ?? {
              providerEventId: state.lastEventId,
            },
          }

      return {
        agentType: input.agentType,
        snapshot: reconcileRuntimeSnapshot(existingSnapshot, nextSnapshot),
        rawOpenCodeProjection: nextRawOpenCodeProjection,
        rawClaudeProjection: nextRawClaudeProjection,
        rawCursorProjection: nextRawCursorProjection,
        stateVersion: state.stateVersion + 1,
      }
    })
  },

  commitComputedRawSyncEventsBatch: (input) => {
    if (get().stateVersion !== input.expectedStateVersion) {
      return false
    }

    if (input.computed.didChange) {
      set((state) => ({
        ...input.computed.nextState,
        stateVersion: state.stateVersion + 1,
      }))
    }

    return true
  },

  commitRuntimeState: (input) => {
    if (get().stateVersion !== input.expectedStateVersion) {
      return false
    }

    set((state) => ({
      ...input.nextState,
      stateVersion: state.stateVersion + 1,
    }))

    return true
  },

  reset: () => {
    set((state) => ({
      ...emptyStoreSnapshot(),
      stateVersion: state.stateVersion + 1,
      rawOpenCodeProjection: null,
      rawClaudeProjection: null,
      rawCursorProjection: null,
    }))
  },
}))
